import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../config/axios';

interface SellerMedicine {
  seller_medicine_id: number;
  batch_number: string;
  seller_id: number;
  medicine_id: number;
  unit_price: number;
  seller_stock_quantity: number;
  expiry_date: string;
  discount_percentage: number;
  hike_percent: number;
  is_active: boolean;
  destroyed_quantity: number;
  expired_quantity: number;
  created_at: string;
  updated_at: string;
}

interface SellerMedicineState {
  sellerMedicines: SellerMedicine[];
  loading: boolean;
  error: string | null;
  pagination: {
    current: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

const initialState: SellerMedicineState = {
  sellerMedicines: [],
  loading: false,
  error: null,
  pagination: {
    current: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  },
};

export const createSellerMedicine = createAsyncThunk(
  'sellerMedicines/createSellerMedicine',
  async (sellerMedicineData: any) => {
    const response = await apiClient.post('/seller-medicines', sellerMedicineData);
    return response.data;
  }
);

export const fetchSellerMedicines = createAsyncThunk(
  'sellerMedicines/fetchSellerMedicines',
  async (params: { page?: number; limit?: number; seller_id?: number; medicine_id?: number } = {}) => {
    const { page = 1, limit = 10, seller_id, medicine_id } = params;
    
    // First, fetch seller medicines
    const sellerMedicinesResponse = await apiClient.get('/seller-medicines', {
      params: { page, limit, seller_id, medicine_id }
    });
    
    // Then, fetch all medicines to get category information
    const medicinesResponse = await apiClient.get('/medicines', {
      params: { page: 1, limit: 1000 } // Get all medicines to match with seller medicines
    });
    
    return {
      sellerMedicines: sellerMedicinesResponse.data,
      medicines: medicinesResponse.data
    };
  }
);

export const updateSellerMedicine = createAsyncThunk(
  'sellerMedicines/updateSellerMedicine',
  async ({ id, sellerMedicineData }: { id: number; sellerMedicineData: Partial<SellerMedicine> }, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(`/seller-medicines/${id}`, sellerMedicineData);
      return response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to update seller medicine';
      return rejectWithValue({ message: errorMessage });
    }
  }
);

export const deleteSellerMedicine = createAsyncThunk(
  'sellerMedicines/deleteSellerMedicine',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await apiClient.delete(`/seller-medicines/${id}`);
      return response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to delete seller medicine';
      return rejectWithValue({ message: errorMessage });
    }
  }
);

const sellerMedicineSlice = createSlice({
  name: 'sellerMedicines',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSellerMedicines.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSellerMedicines.fulfilled, (state, action) => {
        state.loading = false;
        console.log('Seller medicines API response:', action.payload);
        
        let sellerMedicines: any[] = [];
        let medicines: any[] = [];
        
        // Process seller medicines
        if (Array.isArray(action.payload.sellerMedicines.data)) {
          sellerMedicines = action.payload.sellerMedicines.data.map((item: any) => ({
            seller_medicine_id: item.id,
            batch_number: item.attributes?.batch_number || item.batch_number,
            seller_id: item.attributes?.seller?.data?.id || item.seller_id,
            medicine_id: item.attributes?.medicine?.data?.id || item.medicine_id,
            // Seller medicine specific fields
            unit_price: item.attributes?.unit_price || item.unit_price || 0,
            seller_stock_quantity: item.attributes?.seller_stock_quantity || item.seller_stock_quantity || 0,
            expiry_date: item.attributes?.expiry_date || item.expiry_date || new Date().toISOString().split('T')[0],
            discount_percentage: item.attributes?.discount_percentage || item.discount_percentage || 0,
            hike_percent: item.attributes?.hike_percent || item.hike_percent || 0,
            is_active: item.attributes?.is_active !== undefined ? item.attributes.is_active : item.is_active,
            destroyed_quantity: item.attributes?.destroyed_quantity || item.destroyed_quantity || 0,
            expired_quantity: item.attributes?.expired_quantity || item.expired_quantity || 0,
            created_at: item.attributes?.createdAt || item.created_at,
            updated_at: item.attributes?.updatedAt || item.updated_at,
          }));
        }
        
        // Process medicines to get category information
        if (Array.isArray(action.payload.medicines.data)) {
          medicines = action.payload.medicines.data.map((item: any) => ({
            medicine_id: item.id,
            medicine_name: item.attributes?.medicine_name || item.medicine_name,
            category_id: item.attributes?.category?.data?.id || item.category_id || null,
            category_name: (item.attributes?.category?.data && item.attributes.category.data.id) 
              ? item.attributes.category.data.attributes?.category_name 
              : (item.category_name || 'No Category'),
            description: item.attributes?.description || item.description || '',
            is_active: item.attributes?.is_active !== undefined ? item.attributes.is_active : item.is_active,
            created_at: item.attributes?.createdAt || item.created_at,
            updated_at: item.attributes?.updatedAt || item.updated_at,
          }));
        }
        
        // Merge seller medicines with medicine data
        const mergedSellerMedicines = sellerMedicines.map((sellerMedicine: any) => {
          const medicine = medicines.find((med: any) => med.medicine_id === sellerMedicine.medicine_id);
          console.log('Merging seller medicine:', sellerMedicine.medicine_id, 'with medicine:', medicine);
          return {
            ...sellerMedicine,
            medicine_name: medicine?.medicine_name || 'Unknown Medicine',
            category_id: medicine?.category_id || null,
            category_name: medicine?.category_name || 'No Category',
            description: medicine?.description || '',
          };
        });
        
        console.log('Final merged seller medicines:', mergedSellerMedicines);
        
        state.sellerMedicines = mergedSellerMedicines;
        state.pagination = action.payload.sellerMedicines.pagination || action.payload.sellerMedicines.meta?.pagination || {
          current: 1,
          pageSize: 10,
          total: 0,
          totalPages: 0,
        };
      })
      .addCase(fetchSellerMedicines.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch seller medicines';
      })
      .addCase(createSellerMedicine.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSellerMedicine.fulfilled, (state, action) => {
        state.loading = false;
        console.log('Create seller medicine response:', action.payload);
        
        let newSellerMedicine = action.payload.data || action.payload;
        if (newSellerMedicine && newSellerMedicine.attributes) {
          newSellerMedicine = {
            seller_medicine_id: newSellerMedicine.id,
            batch_number: newSellerMedicine.attributes.batch_number,
            seller_id: newSellerMedicine.attributes.seller?.data?.id || newSellerMedicine.seller_id,
            medicine_id: newSellerMedicine.attributes.medicine?.data?.id || newSellerMedicine.medicine_id,
            unit_price: newSellerMedicine.attributes.unit_price || 0,
            seller_stock_quantity: newSellerMedicine.attributes.seller_stock_quantity || 0,
            expiry_date: newSellerMedicine.attributes.expiry_date || new Date().toISOString().split('T')[0],
            discount_percentage: newSellerMedicine.attributes.discount_percentage || 0,
            hike_percent: newSellerMedicine.attributes.hike_percent || 0,
            is_active: newSellerMedicine.attributes.is_active,
            destroyed_quantity: newSellerMedicine.attributes.destroyed_quantity || 0,
            expired_quantity: newSellerMedicine.attributes.expired_quantity || 0,
            created_at: newSellerMedicine.attributes.createdAt,
            updated_at: newSellerMedicine.attributes.updatedAt,
          };
        }
        
        state.sellerMedicines.push(newSellerMedicine);
      })
      .addCase(createSellerMedicine.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create seller medicine';
      })
      .addCase(updateSellerMedicine.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSellerMedicine.fulfilled, (state, action) => {
        state.loading = false;
        const updatedSellerMedicine = action.payload.data || action.payload;
        const index = state.sellerMedicines.findIndex(
          (sm) => sm.seller_medicine_id === updatedSellerMedicine.id
        );
        if (index !== -1) {
          state.sellerMedicines[index] = { ...state.sellerMedicines[index], ...updatedSellerMedicine };
        }
      })
      .addCase(updateSellerMedicine.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update seller medicine';
      })
      .addCase(deleteSellerMedicine.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteSellerMedicine.fulfilled, (state, action) => {
        state.loading = false;
        const deletedId = action.meta.arg;
        state.sellerMedicines = state.sellerMedicines.filter(
          (sm) => sm.seller_medicine_id !== deletedId
        );
      })
      .addCase(deleteSellerMedicine.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete seller medicine';
      });
  },
});

export default sellerMedicineSlice.reducer;
